import { MAX_WINDOWS, anchoredOpening, defaultWindowLayout, resolveWindowLayout } from './windowLayout.js';
import { readGroovePattern, resolveLeafGrooves } from './leafGrooves.js';
import { readCottagePattern, leafGroovesForFamily } from './timberLeafSurface.js';

const copy = (value) => JSON.parse(JSON.stringify(value));

// Library/published designs are patterns. Full construction belongs to the workspace or a render job.
// Selecting fields explicitly also makes older, full-geometry saves behave as patterns.
export function readDoorPattern(input) {
  if (!input || typeof input.name !== 'string' || !input.name.trim() || input.name.length > 200 ||
      !Array.isArray(input.openings) || input.openings.length > MAX_WINDOWS) {
    throw new Error('A design needs a name and up to 10 window openings.');
  }
  const layout = input.windowLayout || (input.version === 1 ? { ...defaultWindowLayout(), mode: 'individual' } : null);
  if (!layout) throw new Error('The design needs window anchors and spacing rules.');
  const openings = input.openings.map((opening) => {
    if (!opening || ![opening.widthMm, opening.heightMm].every((n) => Number.isFinite(n) && n > 0 && n <= 4000)) {
      throw new Error('Window dimensions must be positive numbers up to 4000 mm.');
    }
    const source = !opening.anchorX && Number.isFinite(opening.xMm) && Number.isFinite(opening.yMm)
      ? anchoredOpening(opening) : opening;
    const result = { widthMm: source.widthMm, heightMm: source.heightMm };
    for (const key of ['anchorX', 'anchorY']) if (source[key]) {
      result[key] = { edge: source[key].edge, offsetMm: source[key].offsetMm ?? 0,
        ...(source[key].edge === 'stretch' ? { endOffsetMm: source[key].endOffsetMm } : {}),
        ...(source[key].edge === 'ratio' ? { ratio: source[key].ratio ?? 0.5 } : {}) };
    }
    if (layout.mode === 'individual' && (!result.anchorX || !result.anchorY)) throw new Error('Individual windows need horizontal and vertical anchors.');
    return result;
  });
  const sourceGrooves = leafGroovesForFamily(input.family, input.grooves);
  const grooves = readGroovePattern(sourceGrooves);
  const cottage = input.family === 'COTTAGE' || (!input.family && input.cottagePattern !== undefined);
  const pattern = copy({ name: input.name, openings, windowLayout: layout, grooves,
    ...(cottage ? { cottagePattern: readCottagePattern(input.cottagePattern) } : {}) });
  // Validate the pattern's rules independently of the currently configured door.
  // Actual fit is checked by validateLeafDesign after applying it to a workspace.
  resolveWindowLayout({ ...pattern, widthMm: 4000, heightMm: 4000 });
  if (grooves.mode !== 'custom') resolveLeafGrooves({ widthMm: 4000, heightMm: 4000, grooves });
  return pattern;
}

export function applyDoorPattern(configuration, pattern) {
  return { ...configuration, ...readDoorPattern(pattern) };
}
